import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Phone, PhoneOff, Voicemail, CheckCircle2, Clock, RefreshCw, MessageSquareQuote } from "lucide-react";

const STATUS_STYLES = {
  queued: { icon: Clock, label: "Queued", color: "bg-neutral-100 text-neutral-700 border-neutral-300" },
  "in-progress": { icon: Phone, label: "Live", color: "bg-blue-50 text-blue-700 border-blue-300" },
  ringing: { icon: Phone, label: "Ringing", color: "bg-blue-50 text-blue-700 border-blue-300" },
  completed: { icon: CheckCircle2, label: "Completed", color: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  ended: { icon: CheckCircle2, label: "Ended", color: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  failed: { icon: PhoneOff, label: "Failed", color: "bg-red-50 text-red-700 border-red-300" },
  "no-answer": { icon: PhoneOff, label: "No answer", color: "bg-amber-50 text-amber-800 border-amber-300" },
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "live", label: "Live now" },
  { key: "voicemail", label: "Voicemail" },
  { key: "needs_followup", label: "Needs VA follow-up" },
  { key: "completed", label: "Completed" },
  { key: "failed", label: "Failed" },
];

export default function Outreach() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [vapiStatus, setVapiStatus] = useState(null);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    try {
      const [{ data: c }, { data: s }] = await Promise.all([
        api.get("/vapi/calls?limit=200"),
        api.get("/vapi/status"),
      ]);
      setCalls(c.calls || []);
      setVapiStatus(s);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // poll every 15s for live updates
    return () => clearInterval(t);
  }, []);

  const filtered = useMemo(() => {
    return (calls || []).filter((c) => {
      if (filter === "all") return true;
      if (filter === "live") return ["in-progress", "ringing", "queued"].includes(c.status);
      if (filter === "voicemail") return c.voicemail_detected;
      if (filter === "needs_followup")
        return c.voicemail_detected || c.ended_reason === "customer-did-not-answer" || c.ended_reason === "customer-busy";
      if (filter === "completed") return c.status === "completed";
      if (filter === "failed") return c.status === "failed" || (c.ended_reason && c.ended_reason.startsWith("call.start.error"));
      return true;
    });
  }, [calls, filter]);

  const stats = useMemo(() => {
    return {
      total: calls.length,
      live: calls.filter((c) => ["in-progress", "ringing", "queued"].includes(c.status)).length,
      voicemail: calls.filter((c) => c.voicemail_detected).length,
      needs: calls.filter((c) => c.voicemail_detected || ["customer-did-not-answer", "customer-busy"].includes(c.ended_reason || "")).length,
      completed: calls.filter((c) => c.status === "completed").length,
    };
  }, [calls]);

  return (
    <div className="p-10" data-testid="outreach-page">
      <Toaster position="top-right" richColors />
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="overline mb-2">Lead engine · outreach</div>
          <h1 className="font-display text-4xl font-black tracking-tight">AI Outreach Console</h1>
          <p className="text-neutral-600 mt-2 max-w-2xl">
            Every call your AI agent has placed, live-updated every 15 seconds. Route voicemails and no-answers to your VA.
          </p>
        </div>
        <div className="text-right space-y-1">
          <div className="text-xs uppercase tracking-widest text-neutral-500">Vapi status</div>
          {vapiStatus ? (
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 border ${vapiStatus.configured ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-red-50 border-red-200 text-red-800"}`}>
              <span className={`w-2 h-2 rounded-full ${vapiStatus.configured ? "bg-emerald-500" : "bg-red-500"}`} />
              {vapiStatus.configured ? "Connected" : "Not configured"}
            </div>
          ) : (
            <span className="text-neutral-400 text-sm">…</span>
          )}
          {vapiStatus?.configured && !vapiStatus?.phone_number_id && (
            <div className="text-[10px] text-amber-700 max-w-[220px]">
              Import a Twilio number in your Vapi dashboard and paste its <code>phoneNumberId</code> into <code>VAPI_PHONE_NUMBER_ID</code>.
            </div>
          )}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <Kpi label="Total calls" value={stats.total} />
        <Kpi label="Live" value={stats.live} accent="#002FA7" />
        <Kpi label="Voicemail" value={stats.voicemail} />
        <Kpi label="VA follow-up" value={stats.needs} accent="#E24E1B" />
        <Kpi label="Completed" value={stats.completed} accent="#059669" />
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`outreach-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-1.5 text-xs font-semibold uppercase tracking-widest border transition ${
              filter === f.key ? "bg-[#0F2340] text-white border-[#0F2340]" : "bg-white text-neutral-600 border-neutral-300 hover:border-neutral-500"
            }`}
          >
            {f.label}
          </button>
        ))}
        <button onClick={load} className="ml-auto lf-btn-outline text-xs py-1.5 px-3 flex items-center gap-1">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-neutral-200">
        {loading ? (
          <div className="p-10 text-neutral-500">Loading calls…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-neutral-500">
            No calls in this view. Start one from the Pipeline → click any lead → &quot;Call with AI&quot;.
          </div>
        ) : (
          <table className="lf-table w-full">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Reason / signal</th>
                <th>Started</th>
                <th>VA action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const S = STATUS_STYLES[c.status] || STATUS_STYLES.queued;
                const Icon = S.icon;
                return (
                  <tr key={c.id} data-testid={`outreach-row-${c.id}`}>
                    <td>
                      <Link to={`/leads/${c.lead_id}`} className="font-semibold hover:underline">
                        {c.lead_name}
                      </Link>
                      <div className="text-xs text-neutral-500">{c.lead_industry} · {c.lead_city}</div>
                    </td>
                    <td className="font-mono text-sm">{c.phone}</td>
                    <td>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-xs ${S.color}`}>
                        <Icon className="w-3 h-3" /> {S.label}
                      </span>
                    </td>
                    <td className="text-sm">
                      {c.voicemail_detected && (
                        <span className="inline-flex items-center gap-1 text-amber-800 font-medium">
                          <Voicemail className="w-3.5 h-3.5" /> Voicemail
                        </span>
                      )}
                      {!c.voicemail_detected && c.ended_reason && (
                        <span className="text-neutral-600 text-xs font-mono">{c.ended_reason}</span>
                      )}
                    </td>
                    <td className="text-xs text-neutral-500 font-mono">
                      {c.created_at ? new Date(c.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="text-right">
                      <button
                        onClick={() => setSelected(c)}
                        data-testid={`outreach-view-${c.id}`}
                        className="lf-btn-outline text-xs py-1 px-2 inline-flex items-center gap-1"
                      >
                        <MessageSquareQuote className="w-3 h-3" /> View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail drawer */}
      {selected && <CallDrawer call={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="bg-white border border-neutral-200 p-4">
      <div className="overline mb-1">{label}</div>
      <div className="font-display text-3xl font-bold" style={{ color: accent || "#0F2340" }}>{value}</div>
    </div>
  );
}

function CallDrawer({ call, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-xl bg-white h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-6">
          <div>
            <div className="overline mb-1">Call detail</div>
            <h2 className="font-display text-2xl font-bold">{call.lead_name}</h2>
            <div className="text-sm text-neutral-500 mt-1">{call.phone} · {call.lead_city}</div>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-black text-xl">×</button>
        </div>
        <div className="space-y-3 mb-6">
          <DetailRow label="Status" value={call.status} />
          <DetailRow label="Voicemail?" value={call.voicemail_detected ? "Yes" : "No"} />
          <DetailRow label="Ended reason" value={call.ended_reason || "—"} />
          <DetailRow label="Vapi call ID" value={<code className="text-xs">{call.vapi_call_id}</code>} />
        </div>
        {call.summary && (
          <div className="bg-neutral-50 p-4 border border-neutral-200 mb-4">
            <div className="overline mb-2">Summary</div>
            <p className="text-sm">{call.summary}</p>
          </div>
        )}
        <div className="overline mb-2">Transcript</div>
        {(call.transcripts || []).length === 0 && !call.full_transcript ? (
          <div className="text-neutral-500 text-sm">No transcript yet — arrives via webhook as the call progresses.</div>
        ) : call.full_transcript ? (
          <pre className="text-xs whitespace-pre-wrap font-mono bg-neutral-50 p-3 border border-neutral-200">{call.full_transcript}</pre>
        ) : (
          <div className="space-y-2">
            {call.transcripts.map((t, i) => (
              <div key={i} className={`p-2 border-l-2 ${t.role === "assistant" ? "border-[#E24E1B] bg-orange-50" : "border-neutral-400 bg-neutral-50"}`}>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">{t.role}</div>
                <div className="text-sm">{t.text}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between border-b border-neutral-100 pb-2">
      <span className="text-xs uppercase tracking-widest text-neutral-500 font-semibold">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}
